/**
 * Progress: streak, adherence, calendar heatmap, and per-exercise charts.
 *
 * The streak and adherence block is gated behind `has_enough_history`. Showing
 * "0 week streak, 0% adherence" to somebody on day three is actively
 * discouraging — the exact opposite of what an adherence feature is for.
 */

import { useEffect, useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import { api } from '../api/client'
import { exerciseHistory, getExercises, lastTrainedByExercise } from '../db/queries'
import { bodyweightFactor, measuresOf, weightIsAssist, type Exercise, type SetRow } from '../db'
import { ExerciseSearch } from '../components/ExerciseSearch'
import { recentSessions } from '../lib/recentSessions'
import { formatRound, groupRounds, hasSides, rowsOf } from '../lib/rounds'
import { describeSets } from '../lib/progression'
import { formatSet } from '../lib/setFormat'
import { unitFor, useDistanceOverrides } from '../lib/distance'
import { ProgressChart, type Point } from '../components/ProgressChart'
import { BodyWeightCard } from '../components/BodyWeight'
import { formatDistance, formatDuration, formatPace, formatWeight,
  type DistancePref, type DistanceUnit, type Unit } from '../lib/units'
import { dayIso, dayOf } from '../lib/day'
import { parseIso } from '../lib/time'

type Summary = {
  heatmap: Record<string, number>
  week_streak: number
  sessions_28d: number
  adherence: { due: number; completed: number; pct: number | null }
  has_enough_history: boolean
  history_days: number
  volume: {
    weeks: { week_start: string; volume_kg: number }[]
    this_week_kg: number
    /** null until there is a week to compare against. */
    prior_4wk_avg_kg: number | null
  }
  category_sets: { category: string; sets: number }[]
  recent_prs: {
    /** Sent by PersonalRecord.to_dict since the first migration; this type
     *  omitted it, so it arrived and was thrown away — the same way
     *  target_weight_kg was lost on PlanItem. */
    exercise_id: number
    exercise_name: string | null
    kind: string
    value: number
    reps: number | null
    weight_kg: number | null
    achieved_at: string | null
  }[]
  prs_this_month: number
}

type ExerciseStats = {
  unit: string
  points: { date: string; e1rm: number | null; top_weight: number | null;
            volume: number; sets: number }[]
  prs: { kind: string; value: number; reps: number | null;
         weight_kg: number | null; achieved_at: string | null }[]
}

export function ProgressScreen({
  unit,
  distancePref,
}: {
  unit: Unit
  distancePref: DistancePref
}) {
  const exercises = useLiveQuery(getExercises, [], [] as Exercise[])
  const lastTrained = useLiveQuery(lastTrainedByExercise, [], new Map<number, string>())
  const distanceOverrides = useDistanceOverrides()
  const [summary, setSummary] = useState<Summary | null>(null)
  const [exerciseId, setExerciseId] = useState<number | null>(null)
  const [stats, setStats] = useState<ExerciseStats | null>(null)
  const chosen = exercises.find((e) => e.id === exerciseId)
  const assisted = weightIsAssist(chosen)
  const carriesBody = bodyweightFactor(chosen) > 0
  // Bodyweight work whose share of you nobody can state — a push up, an
  // inverted row, a nordic curl.
  //
  // The `weight` measure is what keeps a plank out of it. A plank is
  // `is_bodyweight` with no factor too, but it has no weight box at all, so it
  // is not withholding a number — it simply measures seconds, and the records
  // below already say so. Explaining an absence nobody noticed is noise.
  const noLoad = Boolean(chosen?.is_bodyweight) && !carriesBody
    && measuresOf(chosen).includes('weight')
  // Metres or miles for THIS exercise's records — a 22 m carry and a 5 km row
  // cannot share a unit, and the PR payload does not carry the exercise row.
  const exerciseDistanceUnit = unitFor(
    chosen, { unit, pref: distancePref, overrides: distanceOverrides })
  const [loading, setLoading] = useState(false)
  const [offline, setOffline] = useState(false)

  useEffect(() => {
    api<Summary>('/stats/summary')
      .then((s) => { setSummary(s); setOffline(false) })
      .catch(() => setOffline(true))
  }, [])

  useEffect(() => {
    if (!exerciseId) return
    setLoading(true)
    api<ExerciseStats>(`/stats/exercise/${exerciseId}?days=365`)
      .then(setStats)
      .catch(() => setStats(null))
      .finally(() => setLoading(false))
  }, [exerciseId])

  const e1rmPoints: Point[] = useMemo(() => {
    if (!stats) return []
    const bestPR = stats.prs.find((p) => p.kind === 'e1rm')
    // The server keys points by LOCAL day, so the PR's day has to be local
    // too — sliced off the UTC clock it marked the wrong point all evening.
    const prDate = bestPR?.achieved_at ? dayOf(bestPR.achieved_at) : undefined
    return stats.points.map((p) => ({
      date: p.date,
      value: p.e1rm,
      isPR: prDate ? p.date === prDate : false,
    }))
  }, [stats])

  const volumePoints: Point[] = useMemo(
    () => (stats ? stats.points.map((p) => ({ date: p.date, value: p.volume })) : []),
    [stats],
  )

  // Whether these two charts have anything to say, decided from the DATA rather
  // than from the exercise.
  //
  // A plank, a 5k and a set of push ups all have no load: nothing is on the bar
  // and there is no bar. The volume chart drew them anyway — a single point
  // plotted at 0, which reads as a measurement rather than as an absence — and
  // the estimated-1RM chart sat above it saying "not enough data yet" forever.
  //
  // Data-driven, because the same exercise answers both ways: a crunch with a
  // plate on your chest has a real load and a crunch without one does not, and
  // `is_bodyweight` cannot tell them apart. If a weight was ever logged, the
  // charts appear; if not, they are absent rather than empty.
  const hasE1rm = e1rmPoints.some((p) => p.value != null)
  const hasVolume = volumePoints.some((p) => (p.value ?? 0) > 0)

  return (
    <>
      <h1>Progress</h1>

      {offline && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <span className="muted">
            Stats are computed on the server — they'll appear when you're back online.
            Your logging is unaffected.
          </span>
        </div>
      )}

      {summary?.has_enough_history && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            <Stat
              value={String(summary.week_streak)}
              label={`week${summary.week_streak === 1 ? '' : 's'} in a row`}
            />
            <Stat value={String(summary.sessions_28d)} label="sessions, 28 days" />
            {summary.adherence.pct !== null && (
              <Stat value={`${summary.adherence.pct}%`} label="of plan hit" />
            )}
          </div>
          <p className="muted" style={{ margin: '.75rem 0 0', fontSize: '.82rem' }}>
            A week counts once you've trained twice in it — nobody lifts seven
            days a week, so a daily streak would just break immediately.
          </p>
        </div>
      )}

      {/* Above the server-backed cards on purpose: this one reads Dexie, so it
          renders offline and it renders immediately, while everything below it
          waits on /stats/summary. It is also the only card here you can write
          to, which makes it the wrong thing to bury. */}
      <BodyWeightCard unit={unit} />

      {summary && (
        <RecentPRs summary={summary} unit={unit} distancePref={distancePref}
                   exercises={exercises} />
      )}
      {summary && <VolumeTrend volume={summary.volume} unit={unit} />}
      {summary && <CategoryBalance categories={summary.category_sets} />}

      {summary && <Heatmap heatmap={summary.heatmap} />}

      <h2>Per exercise</h2>
      <div className="card">
        <ExerciseSearch
          exercises={exercises}
          lastTrained={lastTrained}
          value={exerciseId}
          onChange={setExerciseId}
        />
      </div>

      {/* Above the charts, and read from the device rather than the server: it
          is the part you look at between sets, so it has to be there in a
          basement gym while the charts wait on a connection. */}
      {chosen && (
        <RecentSessionsCard
          exercise={chosen}
          unit={unit}
          distanceUnit={exerciseDistanceUnit}
        />
      )}

      {loading && <p className="muted">Loading…</p>}

      {/* Everywhere the person is part of the lift, these numbers are the weight
          MOVED, not the number you set. On an assist machine the two even run in
          opposite directions, so a chart climbing while the pin goes down needs
          a sentence or it reads as an error. */}
      {carriesBody && !loading && (
        <p className="muted" style={{ marginTop: '.5rem' }}>
          {assisted
            ? 'These are the weight you moved — your body weight minus the help.'
              + ' Less assistance is a bigger number here.'
            : 'These are the weight you moved — your body weight, plus anything'
              + ' you added on a belt or in a vest.'}
        </p>
      )}

      {stats && !loading && (
        <div style={{ marginTop: '1rem' }}>
          {hasE1rm && (
            <ProgressChart
              title="Estimated 1RM"
              points={e1rmPoints}
              unit={unit}
              tone="primary"
            />
          )}
          {hasVolume && (
            <ProgressChart
              title="Volume per session"
              points={volumePoints}
              unit={unit}
              tone="secondary"
              format={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v)))}
            />
          )}
          {!hasE1rm && !hasVolume && noLoad && (
            <p className="muted">
              Reps are the measure here — how much of you this moves depends on
              your leverage, and the app would rather say nothing than guess. Add
              weight in a vest or on a belt and it will chart it.
            </p>
          )}

          {stats.prs.length > 0 && (
            <>
              <h2>Personal records</h2>
              <ul className="plain">
                {stats.prs.map((pr) => (
                  <li key={pr.kind}>
                    <span>{prLabel(pr.kind)}</span>
                    <span>
                      {formatPR(pr, unit, exerciseDistanceUnit)}
                      {pr.achieved_at && (
                        <span className="muted">
                          {' '}· {parseIso(pr.achieved_at).toLocaleDateString(
                            [], { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <h2>Export</h2>
      <div className="card">
        <p className="muted" style={{ marginTop: 0 }}>
          Every set you've logged, as a CSV. Your data should never be trapped
          in here.
        </p>
        <button
          className="secondary"
          onClick={() => { window.location.href = '/api/v1/export.csv' }}
        >
          Download CSV
        </button>
      </div>
    </>
  )
}

const RECENT_SESSIONS = 5

/**
 * The last few sessions of the chosen exercise: when, how many sets, and the
 * sets themselves, in the same words the Log screen's "Last time" line uses.
 *
 * Five, because that is a month or so of training something weekly — enough to
 * see whether the reps have been creeping up — and still short enough to read
 * without scrolling past the charts.
 */
function RecentSessionsCard({
  exercise,
  unit,
  distanceUnit,
}: {
  exercise: Exercise
  unit: Unit
  distanceUnit: DistanceUnit
}) {
  // Enough rows for five sessions of even a long, two-sided exercise.
  const rows = useLiveQuery(
    () => exerciseHistory(exercise.id, 300),
    [exercise.id],
  )
  const sessions = useMemo(() => (rows ? recentSessions(rows, RECENT_SESSIONS) : undefined),
    [rows])
  const assisted = weightIsAssist(exercise)
  const ms = measuresOf(exercise)

  if (!sessions) return null

  const describe = (sets: SetRow[]): string => {
    if (sets.length === 0) return 'Warm-up only'
    if (hasSides(sets)) {
      return groupRounds(sets)
        .map((round) => formatRound(round, (s) =>
          formatSet(s, exercise, unit, { withRpe: false, du: distanceUnit })))
        .join(', ')
    }
    if (ms.includes('weight') && ms.includes('reps')) {
      return describeSets(sets, (kg) => formatWeight(kg, unit), assisted)
    }
    return sets.map((s) => formatSet(s, exercise, unit, { withRpe: false, du: distanceUnit }))
      .join(', ')
  }

  return (
    <>
      <h2>Recent sessions</h2>
      <div className="card">
        {sessions.length === 0 ? (
          <span className="muted">You haven't logged this exercise yet.</span>
        ) : (
          <ul className="plain" style={{ margin: 0 }}>
            {sessions.map((s) => {
              const working = s.rounds.flatMap(rowsOf)
              return (
                <li key={s.sessionId} style={{ display: 'block', padding: '.55rem 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.5rem' }}>
                    <strong>{sessionDate(s.performedAt)}</strong>
                    <span className="muted" style={{ fontSize: '.9rem', whiteSpace: 'nowrap' }}>
                      {s.setCount} set{s.setCount === 1 ? '' : 's'}
                      {s.warmupCount > 0 && ` + ${s.warmupCount} warm-up`}
                    </span>
                  </div>
                  <div className="muted" style={{ fontSize: '.9rem', marginTop: '.15rem' }}>
                    {describe(working)}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </>
  )
}

function sessionDate(iso: string): string {
  const then = parseIso(iso)
  const today = dayIso(new Date())
  const day = dayOf(iso)
  if (day === today) return 'Today'
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  if (day === dayIso(yesterday)) return 'Yesterday'
  return then.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div style={{ fontSize: '1.6rem', fontWeight: 600, lineHeight: 1.1 }}>{value}</div>
      <div className="muted" style={{ fontSize: '.8rem' }}>{label}</div>
    </div>
  )
}

/**
 * Records, across every exercise.
 *
 * These already existed. They were reachable only by selecting the one exercise
 * they belonged to, which means you had to already know you'd set one — so the
 * single most encouraging thing the app knows was also the best hidden.
 */
function RecentPRs({
  summary,
  unit,
  distancePref,
  exercises,
}: {
  summary: Summary
  unit: Unit
  distancePref: DistancePref
  /** Only for the distance scale — a 22 m carry record and a 5 km row record
   *  cannot share a unit, and the PR payload doesn't carry the exercise row. */
  exercises: Exercise[]
}) {
  if (summary.recent_prs.length === 0) return null
  const byId = new Map(exercises.map((e) => [e.id, e]))
  const overrides = useDistanceOverrides()

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="row" style={{ marginBottom: '.4rem' }}>
        <strong style={{ fontSize: '.95rem' }}>Records</strong>
        <span className="muted" style={{ fontSize: '.82rem' }}>
          {summary.prs_this_month} this month
        </span>
      </div>
      <ul className="plain" style={{ margin: 0 }}>
        {summary.recent_prs.map((pr) => (
          <li key={`${pr.exercise_name}-${pr.kind}`}>
            <span style={{ minWidth: 0 }}>
              {pr.exercise_name ?? 'Unknown'}
              <span className="muted" style={{ fontSize: '.82rem' }}>
                {' · '}{describePR(pr, unit,
                  unitFor(byId.get(pr.exercise_id), { unit, pref: distancePref, overrides }))}
              </span>
            </span>
            {pr.achieved_at && (
              <span className="muted" style={{ fontSize: '.8rem', whiteSpace: 'nowrap' }}>
                {parseIso(pr.achieved_at).toLocaleDateString(
                  [], { month: 'short', day: 'numeric' })}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A record is only meaningful with its kind attached — "120" is not a fact. */
function describePR(
  pr: Summary['recent_prs'][number],
  unit: Unit,
  du: DistanceUnit = 'km',
): string {
  const w = (kg: number | null) => (kg ? formatWeight(kg, unit) : '—')
  switch (pr.kind) {
    case 'e1rm': return `est. 1RM ${w(pr.value)}`
    case 'max_weight': return `heaviest ${w(pr.value)}${pr.reps ? ` x ${pr.reps}` : ''}`
    case 'max_reps': return `${pr.value} reps${pr.weight_kg ? ` at ${w(pr.weight_kg)}` : ''}`
    // NOT formatWeight(value): that is weight x reps, so rendering it as a
    // weight claims you lifted 1499 lb. Show the set that produced it.
    case 'max_volume': return pr.weight_kg && pr.reps
      ? `best set ${w(pr.weight_kg)} x ${pr.reps}`
      : `best set`
    case 'max_distance': return `furthest ${formatDistance(pr.value, du)}`
    case 'max_duration': return `longest ${formatDuration(pr.value)}`
    case 'max_speed': return `fastest ${formatPace(pr.value, 1, du)}`
    default: return String(pr.value)
  }
}

/**
 * Tonnage by week: weight x reps, working sets only.
 *
 * The one number on this screen that answers "am I doing more than I was".
 * Everything above it measures attendance.
 */
function VolumeTrend({
  volume,
  unit,
}: {
  volume: Summary['volume']
  unit: Unit
}) {
  const points: Point[] = volume.weeks.map((w) => ({
    date: w.week_start,
    value: w.volume_kg || null,
  }))
  if (points.every((p) => p.value === null)) return null

  const baseline = volume.prior_4wk_avg_kg
  const delta = baseline ? Math.round((volume.this_week_kg / baseline - 1) * 100) : null

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="row" style={{ marginBottom: '.2rem' }}>
        <strong style={{ fontSize: '.95rem' }}>Work done</strong>
        <span className="muted" style={{ fontSize: '.82rem', textAlign: 'right' }}>
          {/* "so far". The baseline excludes the week in progress, but the
              comparison is still a partial week against complete ones, so on a
              Monday it reads -80% and means nothing. Saying which it is costs
              two words; leaving it out makes the number a lie every Monday. */}
          {delta === null
            ? 'first week — no baseline yet'
            : `${delta >= 0 ? '+' : ''}${delta}% so far vs your 4-week average`}
        </span>
      </div>
      {/* The chart draws its own caption with the unit; giving it an empty title
          left a bare "(lb)" hanging on its own line under the heading. */}
      <ProgressChart
        title="Weekly tonnage"
        points={points}
        unit={unit}
        tone="secondary"
        height={120}
        format={(v) => formatWeight(v, unit)}
      />
    </div>
  )
}

/**
 * What has actually been trained, over 28 days.
 *
 * One measure across categories, so one hue and a direct label on each bar
 * rather than a palette and a legend — colour here would be decoration standing
 * in for the number that is already written next to it.
 *
 * The gap this catches is invisible from inside a workout: every session looks
 * balanced on its own, and a month of pressing and pulling with five leg sets
 * still looks fine one session at a time.
 */
function CategoryBalance({
  categories,
}: {
  categories: Summary['category_sets']
}) {
  if (categories.length === 0) return null
  const most = Math.max(...categories.map((c) => c.sets))

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="row" style={{ marginBottom: '.5rem' }}>
        <strong style={{ fontSize: '.95rem' }}>Balance</strong>
        <span className="muted" style={{ fontSize: '.82rem' }}>working sets, 28 days</span>
      </div>
      {categories.map((c) => (
        <div key={c.category} style={{ marginBottom: '.35rem' }}>
          <div className="row" style={{ marginBottom: '.1rem' }}>
            <span style={{ fontSize: '.85rem', textTransform: 'capitalize' }}>
              {c.category}
            </span>
            <span className="muted" style={{ fontSize: '.82rem' }}>{c.sets}</span>
          </div>
          <div
            aria-hidden
            style={{
              height: 6,
              borderRadius: 3,
              background: 'var(--surface-2)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.round((c.sets / most) * 100)}%`,
                height: '100%',
                background: 'var(--accent)',
              }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Twelve weeks of don't-break-the-chain.
 *
 * Sequential single hue, light to dark — never a rainbow. Intensity is opacity
 * over the accent, so it reads correctly in both themes without a second
 * palette.
 */
function Heatmap({ heatmap }: { heatmap: Record<string, number> }) {
  const weeks = 12
  const [picked, setPicked] = useState<string | null>(null)

  const { cells, months, today } = useMemo(() => {
    // Anchored to THIS week's Monday, counting backwards.
    //
    // It used to start at today-83 and then roll back to Monday, subtracting up
    // to six more days while still drawing only 84 squares — so the whole window
    // slid earlier and the tail fell off the end. Today never had a square, and
    // on a Sunday the entire current week was missing. Training and then seeing
    // nothing light up is what "it doesn't work" looks like from the outside.
    const monday = new Date()
    monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7))
    const from = new Date(monday)
    from.setDate(from.getDate() - (weeks - 1) * 7)

    const cells: { date: string; count: number; future: boolean }[] = []
    const months: { col: number; label: string }[] = []
    const todayIso = dayIso(new Date())

    for (let i = 0; i < weeks * 7; i++) {
      const d = new Date(from)
      d.setDate(d.getDate() + i)
      // dayIso, not toISOString: the server keys this map by the user's LOCAL
      // day, and toISOString would ask for tomorrow's square all evening.
      const key = dayIso(d)
      cells.push({ date: key, count: heatmap[key] ?? 0, future: key > todayIso })
      // One label per column, at the top of each week that opens a new month.
      if (i % 7 === 0) {
        const col = i / 7
        const label = d.toLocaleDateString([], { month: 'short' })
        if (months.length === 0 || months[months.length - 1].label !== label) {
          months.push({ col, label })
        }
      }
    }
    return { cells, months, today: todayIso }
  }, [heatmap])

  const chosen = picked ? cells.find((c) => c.date === picked) : null
  const trained = cells.filter((c) => c.count > 0).length

  return (
    <figure style={{ margin: '0 0 1.25rem' }}>
      <figcaption style={{ fontSize: '.95rem', marginBottom: '.4rem' }}>
        Last 12 weeks{' '}
        <span className="muted" style={{ fontWeight: 400, fontSize: '.85rem' }}>
          · {trained} day{trained === 1 ? '' : 's'} trained
        </span>
      </figcaption>
      <div className="card" style={{ padding: '.75rem', overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 4, minWidth: 'fit-content' }}>
          {/* Mon/Wed/Fri only — seven labels at this size is a wall of text
              beside a grid that is itself 11px per row. */}
          <div style={{ display: 'grid', gridTemplateRows: 'repeat(7, 11px)', gap: 3,
                        marginTop: 14 }}>
            {['M', '', 'W', '', 'F', '', ''].map((d, i) => (
              <span key={i} className="muted"
                    style={{ fontSize: 9, lineHeight: '11px', width: 10 }}>{d}</span>
            ))}
          </div>

          <div>
            {/* Month labels, so a square can be located at all. The title
                attribute the squares used to carry needs a mouse, and this is a
                phone — there was no way to tell which day you were looking at. */}
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${weeks}, 11px)`,
                          gap: 3, height: 14 }}>
              {Array.from({ length: weeks }, (_, col) => {
                const m = months.find((x) => x.col === col)
                return (
                  <span key={col} className="muted"
                        style={{ fontSize: 9, whiteSpace: 'nowrap' }}>
                    {m ? m.label : ''}
                  </span>
                )
              })}
            </div>

            <div
              role="img"
              aria-label={`Training days over the last 12 weeks: ${trained} days trained.`}
              style={{
                display: 'grid',
                gridTemplateRows: 'repeat(7, 11px)',
                gridAutoFlow: 'column',
                gap: 3,
              }}
            >
              {cells.map((c) => (
                <div
                  key={c.date}
                  onClick={() => setPicked(picked === c.date ? null : c.date)}
                  style={{
                    width: 11,
                    height: 11,
                    borderRadius: 2,
                    cursor: 'pointer',
                    // A day that hasn't happened yet is not a day you skipped.
                    background: c.count === 0
                      ? (c.future ? 'transparent' : 'var(--surface-2)')
                      : 'var(--accent)',
                    border: c.future ? '1px dashed var(--border)' : undefined,
                    opacity: c.count === 0 ? 1 : Math.min(1, 0.45 + c.count * 0.3),
                    outline: c.date === today ? '2px solid var(--cal-today)' : undefined,
                    outlineOffset: c.date === today ? 1 : undefined,
                    boxShadow: c.date === picked
                      ? '0 0 0 2px var(--text)' : undefined,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tap a square to read it. Replaces the title tooltip, which on a
          touchscreen was simply invisible. */}
      <div className="muted" style={{ fontSize: '.8rem', marginTop: '.35rem', minHeight: '1.2em' }}>
        {chosen
          ? `${parseIso(chosen.date).toLocaleDateString([], {
              weekday: 'short', month: 'short', day: 'numeric' })} — ${
              chosen.future ? 'not yet'
                : chosen.count === 0 ? 'rest day'
                  : `${chosen.count} session${chosen.count === 1 ? '' : 's'}`}`
          : 'Tap a square for the day. Today is ringed.'}
      </div>
    </figure>
  )
}

/**
 * Both of these used to cover four of the seven PR kinds, and both fell through
 * to something wrong rather than to nothing.
 *
 * `prLabel` printed the raw kind — a plank's record was headed "max_duration" —
 * and `formatPR` multiplied every value that was not a rep count by the pounds
 * factor and appended the weight unit, so a 90-second plank read **198.4 lb**.
 * Every hold, every carry and every run was affected. It stayed unnoticed
 * because it sat below two charts that a bodyweight exercise never filled in,
 * and hiding those is what brought it into view.
 */
function prLabel(kind: string): string {
  switch (kind) {
    case 'e1rm': return 'Best estimated 1RM'
    case 'max_weight': return 'Heaviest set'
    case 'max_reps': return 'Most reps'
    case 'max_volume': return 'Best single-set volume'
    case 'max_duration': return 'Longest'
    case 'max_distance': return 'Furthest'
    case 'max_speed': return 'Fastest'
    default: return kind
  }
}

function formatPR(
  pr: { kind: string; value: number; reps: number | null; weight_kg: number | null },
  unit: Unit,
  du: DistanceUnit = 'km',
): string {
  switch (pr.kind) {
    case 'max_reps':
      return `${Math.round(pr.value * 10) / 10} reps`
    case 'max_duration':
      return formatDuration(pr.value)
    case 'max_distance':
      return formatDistance(pr.value, du)
    // Stored as metres per second so every comparison stays a `>`; shown as
    // pace, which is the only number a runner reads. One second of it is the
    // whole distance, so the inversion is exact.
    case 'max_speed':
      return formatPace(pr.value, 1, du)
    default:
      return formatWeight(pr.value, unit)
  }
}
